import { forwardRef, type ElementType, type FormHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type LabelHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import classnames from 'src/util/classnames';

export function Form({ className, children, ...props }: FormHTMLAttributes<HTMLFormElement>) {
  return <form className={classnames(className)} {...props}>{children}</form>;
}

interface FormGroupProps extends HTMLAttributes<HTMLDivElement> {
  check?: boolean;
  switch?: boolean;
  row?: boolean;
  floating?: boolean;
  disabled?: boolean;
  tag?: ElementType;
  children?: ReactNode;
}

export function FormGroup({ check, switch: switchProp, row, floating, disabled, tag: Tag = 'div', className, children, ...props }: FormGroupProps) {
  return (
    <Tag
      className={classnames(
        check ? classnames('form-check', switchProp && 'form-switch') : 'mb-3',
        row && 'row',
        floating && 'form-floating',
        disabled && 'disabled',
        className,
      )}
      {...props}
    >
      {children}
    </Tag>
  );
}

interface LabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  check?: boolean;
  for?: string;
  hidden?: boolean;
  children?: ReactNode;
}

export function Label({ check, for: htmlFor, hidden, className, children, ...props }: LabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className={classnames(
        check && 'form-check-label',
        hidden && 'visually-hidden',
        className,
      )}
      {...props}
    >
      {children}
    </label>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  tag?: ElementType;
  type?: string;
  valid?: boolean;
  invalid?: boolean;
  bsSize?: 'sm' | 'lg';
  plaintext?: boolean;
  addon?: boolean;
  innerRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { tag: Tag, type = 'text', valid, invalid, bsSize, plaintext, addon, innerRef, className, ...props },
  ref,
) {
  const validClass = classnames(invalid && 'is-invalid', valid && 'is-valid');

  if (Tag) {
    const AnyTag = Tag as ElementType;
    return <AnyTag className={classnames('form-control', className)} ref={ref ?? innerRef} {...props} />;
  }

  if (type === 'select') {
    return (
      <select
        className={classnames('form-select', bsSize && `form-select-${bsSize}`, validClass, className)}
        ref={ref as React.Ref<HTMLSelectElement>}
        {...(props as SelectHTMLAttributes<HTMLSelectElement>)}
      />
    );
  }

  if (type === 'textarea') {
    return (
      <textarea
        className={classnames('form-control', bsSize && `form-control-${bsSize}`, validClass, className)}
        ref={ref as React.Ref<HTMLTextAreaElement>}
        {...(props as TextareaHTMLAttributes<HTMLTextAreaElement>)}
      />
    );
  }

  if (type === 'checkbox' || type === 'radio') {
    return (
      <input
        type={type}
        className={classnames('form-check-input', validClass, className)}
        ref={ref}
        {...props}
      />
    );
  }

  return (
    <input
      type={type}
      className={classnames(
        plaintext ? 'form-control-plaintext' : 'form-control',
        bsSize && `form-control-${bsSize}`,
        validClass,
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});

interface InputGroupProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'lg';
  children?: ReactNode;
}

export function InputGroup({ size, className, children, ...props }: InputGroupProps) {
  return (
    <div className={classnames('input-group', size && `input-group-${size}`, className)} {...props}>
      {children}
    </div>
  );
}

export const InputGroupText = forwardRef<HTMLSpanElement, HTMLAttributes<HTMLSpanElement>>(function InputGroupText(
  { className, children, ...props },
  ref,
) {
  return (
    <span ref={ref} className={classnames('input-group-text', className)} {...props}>
      {children}
    </span>
  );
});

export function FormFeedback({ valid, className, children, ...props }: HTMLAttributes<HTMLDivElement> & { valid?: boolean }) {
  return (
    <div className={classnames(valid ? 'valid-feedback' : 'invalid-feedback', className)} {...props}>
      {children}
    </div>
  );
}

export function FormText({ className, children, ...props }: HTMLAttributes<HTMLSmallElement>) {
  return (
    <small className={classnames('form-text text-muted', className)} {...props}>
      {children}
    </small>
  );
}
